import React, { useEffect, useRef } from "react";
import { Drawer, Typography } from "antd";
import type { DrawerProps as AntdDrawerProps } from "antd";
import { CloseOutlined } from "@ant-design/icons";

const { Title } = Typography;

export interface RightSidePanelProps extends Omit<AntdDrawerProps, "placement" | "title"> {
	/**
	 * Whether the panel is open
	 */
	open: boolean;

	/**
	 * Callback when the panel should close
	 */
	onClose: () => void;

	/**
	 * Panel title
	 */
	title?: React.ReactNode;

	/**
	 * Panel content
	 */
	children?: React.ReactNode;

	/**
	 * Optional footer content
	 */
	footer?: React.ReactNode;

	/**
	 * Custom width for the panel (default: responsive)
	 */
	width?: string | number;

	/**
	 * Whether to close on backdrop click (default: true)
	 */
	maskClosable?: boolean;

	/**
	 * Whether to close on Escape key (default: true)
	 */
	keyboard?: boolean;

	/**
	 * Custom class name
	 */
	className?: string;

	/**
	 * Custom styles
	 */
	styles?: AntdDrawerProps["styles"];

	/**
	 * Whether to destroy child components on close (default: false)
	 */
	destroyOnClose?: boolean;

	/**
	 * Custom header extra content
	 */
	extra?: React.ReactNode;
}

/**
 * RightSidePanel - A standardized right-side slide-over panel component
 * 
 * Features:
 * - Slides in from the right
 * - Responsive width (full width on mobile, fixed/max width on desktop)
 * - Consistent header with title and close button
 * - Optional footer
 * - Focus management and scroll locking
 * - Accessibility support (aria attributes, keyboard navigation)
 * 
 * Usage:
 * ```tsx
 * <RightSidePanel
 *   open={isOpen}
 *   onClose={handleClose}
 *   title="Edit Client"
 *   footer={<Button onClick={handleSave}>Save</Button>}
 * >
 *   <YourFormContent />
 * </RightSidePanel>
 * ```
 */
const RightSidePanel: React.FC<RightSidePanelProps> = ({
	open,
	onClose,
	title,
	children,
	footer,
	width,
	maskClosable = true,
	keyboard = true,
	className,
	styles,
	destroyOnClose = false,
	extra,
	...rest
}) => {
	const panelRef = useRef<HTMLDivElement>(null);
	const handleClose: NonNullable<AntdDrawerProps["onClose"]> = (event) => {
		event?.stopPropagation?.();
		onClose();
	};

	// Handle Escape key
	useEffect(() => {
		if (!open || !keyboard) return;

		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [open, keyboard, onClose]);

	// Focus management: focus first focusable element when opened
	useEffect(() => {
		if (open && panelRef.current) {
			const focusableElements = panelRef.current.querySelectorAll(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
			);
			const firstFocusable = focusableElements[0] as HTMLElement;
			if (firstFocusable) {
				// Small delay to ensure the drawer animation has started
				setTimeout(() => firstFocusable.focus(), 100);
			}
		}
	}, [open]);

	// Compute responsive width - convert to number for now as Drawer doesn't support responsive objects
	const computedWidth = width ?? 720; // Default to 720px

	// Custom styles with better defaults
	const mergedStyles: AntdDrawerProps["styles"] = (info) => {
		const customStyles = typeof styles === "function" ? styles(info) : styles;
		return {
			header: {
				padding: "16px 24px",
				borderBottom: "1px solid var(--v2-border-subtle)",
				...(customStyles?.header || {}),
			},
			body: {
				padding: "24px",
				overflowY: "auto",
				...(customStyles?.body || {}),
			},
			footer: footer ? {
				padding: "16px 24px",
				borderTop: "1px solid var(--v2-border-subtle)",
				textAlign: "right" as const,
				...(customStyles?.footer || {}),
			} : undefined,
			...customStyles,
		};
	};

	return (
		<Drawer
			{...rest}
			placement="right"
			open={open}
			onClose={handleClose}
			width={computedWidth}
			maskClosable={maskClosable}
			keyboard={keyboard}
			className={className}
			styles={mergedStyles}
			destroyOnClose={destroyOnClose}
			title={
				typeof title === "string" ? (
					<Title level={4} style={{ margin: 0 }}>
						{title}
					</Title>
				) : (
					title
				)
			}
			closeIcon={<CloseOutlined />}
			extra={extra}
			footer={footer}
		>
			<div ref={panelRef}>
				{children}
			</div>
		</Drawer>
	);
};

export default RightSidePanel;
